import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { notifyReimport, tenderStaging } from '../lib/api';
import { Modal } from './ui/Modal';
import { ProgressSteps } from './ui/ProgressSteps';

interface Column { col: number; letter: string; header: string; role: string; labor_co_name?: string; round_number?: number; }
interface BidCol { col: number; labor_co_name: string; round_number: number; }
interface PreviewRow { row_index: number; cells: (string | number | null)[]; }
interface StageResp {
  staging_id: string; sheets: { name: string; rows: number; cols: number }[]; sheet_name: string;
  header_row: number; columns: Column[]; item_label_col: number | null; reference_price_col: number | null;
  bid_columns: BidCol[]; is_standard_template?: boolean; preview_rows: PreviewRow[];
}

export function XlsImportModal({ pid, stagingId, onClose }: { pid: string; stagingId?: string; onClose: () => void }) {
  // stagingId set ⇒ 采购大脑「一键填充」: the workbook was already staged by the Stage-3 pull, so we
  // skip step 1 (upload) and rehydrate the SAME review step a fresh upload would land on.
  const fromPull = !!stagingId;
  const qc = useQueryClient();
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [stage, setStage] = useState<StageResp | null>(null);

  // mapping state (step 2)
  const [itemCol, setItemCol] = useState<number | null>(null);
  const [refCol, setRefCol] = useState<number | null>(null);
  const [dataStart, setDataStart] = useState<number>(0);
  const [bidCols, setBidCols] = useState<BidCol[]>([]);
  const [boqOnly, setBoqOnly] = useState(false);   // 仅导入工程量清单（无报价列）
  const [result, setResult] = useState<any>(null);
  const [notifying, setNotifying] = useState(false);        // 7/2 · post-clear notify-labor prompt
  const [notifiedMsg, setNotifiedMsg] = useState<string | null>(null);

  // Land a StageResp on the review step — identical whether it came from a fresh upload or a pulled staging.
  function populate(data: StageResp) {
    setStage(data);
    setItemCol(data.item_label_col);
    setRefCol(data.reference_price_col);
    setDataStart(data.header_row + 1);
    setBidCols(data.bid_columns);
    setBoqOnly(true); // default to BOQ-only (改改's authoring case); uncheck for the full 比价表 import
    setStep(2);
  }

  async function upload(file: File) {
    setBusy(true); setErr(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`/api/packages/${pid}/xls-stage`, { method: 'POST', body: fd });
      if (!res.ok) throw new Error(await res.text());
      populate(await res.json() as StageResp);
    } catch (e: any) { setErr(String(e?.message ?? e)); }
    finally { setBusy(false); }
  }

  // 采购大脑「一键填充」: rehydrate the staged pull into the review step on open.
  useEffect(() => {
    if (!stagingId) return;
    let cancelled = false;
    setBusy(true); setErr(null);
    tenderStaging(stagingId)
      .then((data) => { if (!cancelled) populate(data as StageResp); })
      .catch((e: any) => { if (!cancelled) setErr(String(e?.message ?? e)); })
      .finally(() => { if (!cancelled) setBusy(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stagingId]);

  async function commit(clearBids = false) {
    if (!stage) return;
    setBusy(true); setErr(null);
    try {
      const body = {
        sheet_name: stage.sheet_name, header_row: stage.header_row, data_start_row: dataStart,
        item_label_col: itemCol, reference_price_col: refCol, bid_columns: boqOnly ? [] : bidCols,
        ...(clearBids ? { clear_bids: true } : {}),
      };
      const res = await fetch(`/api/packages/${pid}/xls-stage/${stage.staging_id}/commit`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      if (!res.ok) {
        const txt = await res.text();
        let b: any = null; try { b = JSON.parse(txt); } catch { /* plain text */ }
        // 改改 6/30: round-1 报价 already exists → offer to clear it and re-import the revised 清单 (round 2).
        // Keeping round-1 bids would need the Work Items tab + 发布更新版; this path is the destructive re-upload.
        if (res.status === 409 && b?.code === 'has_bids' && !clearBids && boqOnly &&
            // 7/2 walkthrough F9: this path ARCHIVES (45ef411) — say so, instead of the old scare-copy
            // claiming "不可恢复" which read as the very data loss the archive feature prevents.
            confirm('本包已有已录报价。整表重传新清单会把现有各轮报价【存档】：招标方仍可在对比表「比家比轮」查看历史，劳务端不再显示，需按新清单重新报价。\n（如只是调整个别行，请改用「工程清单」增改行 +「📣发布更新版」，可保留已录报价。）\n确定存档旧报价并重传新清单？/ Existing rounds will be ARCHIVED (staff-visible history); labor re-quotes the new list. Proceed?')) {
          return commit(true);
        }
        throw new Error(b?.error ?? txt);
      }
      setResult(await res.json());
      setStep(3);
      qc.invalidateQueries({ queryKey: ['package', pid] });
      // 采购大脑「一键填充」: the pull's ledger row flips staged → committed, so refresh it too.
      if (fromPull) qc.invalidateQueries({ queryKey: ['package-tender-sync', pid] });
    } catch (e: any) { setErr(String(e?.message ?? e)); }
    finally { setBusy(false); }
  }

  const colLabel = (c: Column) => `${c.letter} · ${c.header || '(空)'}`;

  const stepState = (n: 1 | 2 | 3) => (step === n ? 'active' as const : step > n ? 'done' as const : 'todo' as const);
  return (
    <Modal onClose={onClose} title={fromPull ? '📥 一键填充（采购大脑拉取）/ Fill from pulled BOQ' : '📥 从 xls 导入 / Import from xls'} width={880}>
        <ProgressSteps steps={[
          { label: fromPull ? '拉取 / Pulled' : '上传 / Upload', state: stepState(1) },
          { label: '映射 / Map', state: stepState(2) },
          { label: '提交 / Commit', state: stepState(3) },
        ]} />

        {err && <div className="card" style={{ borderColor: 'var(--bad)', background: 'var(--bad-soft)', color: 'var(--bad)', fontSize: 13 }}>{err}</div>}

        {step === 1 && (
          <div>
            {fromPull ? (
              <div className="small muted">正在载入采购大脑拉取的工程量清单… / Loading the BOQ pulled from 采购大脑…</div>
            ) : (
              <>
                <p className="muted small">上传 xls。只有工程量清单（项目内容 + 参考价）→ 导入清单；带报价列 → 导入比价表。服务端解析后会让你确认列映射。/ Upload an xls — items only seeds the 工程量清单; with bidder columns it imports the comparison table. The server parses it and lets you confirm the mapping.</p>
                <input type="file" accept=".xls,.xlsx" disabled={busy} onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); }} />
                {busy && <div className="small muted" style={{ marginTop: 8 }}>解析中… / Parsing…</div>}
              </>
            )}
          </div>
        )}

        {step === 2 && stage && (
          <div>
            <div className="row-wrap" style={{ marginBottom: 8 }}>
              <span className="small">工作表 / Sheet: <strong>{stage.sheet_name}</strong></span>
              <span className="small muted">表头行 / header row {stage.header_row} · {stage.columns.length} 列</span>
            </div>

            {/* preview */}
            <div className="cmp-wrap" style={{ maxHeight: 230, overflow: 'auto', marginBottom: 12 }}>
              <table className="cmp" style={{ fontSize: 11.5 }}>
                <thead><tr><th>行</th>{stage.columns.map((c) => <th key={c.col}>{c.letter}<div className="small muted" style={{ fontWeight: 400 }}>{c.header}</div></th>)}</tr></thead>
                <tbody>
                  {stage.preview_rows.slice(1).map((pr) => (
                    <tr key={pr.row_index}>
                      <td className="muted">{pr.row_index}</td>
                      {stage.columns.map((c) => <td key={c.col}>{pr.cells[c.col] == null ? '' : String(pr.cells[c.col])}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* mode selector — BOQ-only (default, 改改's authoring case) vs full 比价表 */}
            {stage.is_standard_template && (
              <div className="small" style={{ color: 'var(--good)', marginBottom: 6 }}>✓ 已识别标准工程量清单表头 — 将按标准列（单位/工程量/特征/做法/品牌…）整表导入。/ Standard 工程量清单 detected — importing the full column set.</div>
            )}
            <label className="row" style={{ gap: 8, marginBottom: 10, cursor: 'pointer' }}>
              <input type="checkbox" checked={boqOnly} onChange={(e) => setBoqOnly(e.target.checked)} />
              <span><strong>仅导入工程量清单</strong>（标准表头，无报价列）/ Import 工程量清单 only — standard template, no quotes</span>
            </label>

            {boqOnly ? (
              <div className="row-wrap" style={{ gap: 16, marginBottom: 10, alignItems: 'flex-end' }}>
                <div className="card small muted" style={{ padding: 10, flex: '1 1 360px' }}>
                  将按标准表头自动识别并导入：项目名称 · 项目特征 · 计量规则 · 单位 · 工程量 · 不含税单价 ·（含税单价→税率）· 界限划分 · 品牌要求 · 备注说明。导入后在「工程量清单」标签核对、增删（支持批量删除）。/ Auto-detects the standard template columns. Review & edit in the Work Items tab after import (bulk-delete supported).
                </div>
                <div className="field" style={{ flex: '0 0 130px', marginBottom: 0 }}>
                  <label>数据起始行 / Data start</label>
                  <input type="number" value={dataStart} onChange={(e) => setDataStart(Number(e.target.value))} />
                </div>
              </div>
            ) : (
              <>
                {/* full 比价表 mapping (item + reference + bidder columns) */}
                <div className="row-wrap" style={{ gap: 16, marginBottom: 10 }}>
                  <div className="field" style={{ flex: '1 1 220px', marginBottom: 0 }}>
                    <label>项目内容列 / Item-label column *</label>
                    <select value={itemCol ?? ''} onChange={(e) => setItemCol(e.target.value === '' ? null : Number(e.target.value))}>
                      <option value="">— 选择 —</option>
                      {stage.columns.map((c) => <option key={c.col} value={c.col}>{colLabel(c)}</option>)}
                    </select>
                  </div>
                  <div className="field" style={{ flex: '1 1 220px', marginBottom: 0 }}>
                    <label>参考价列 / Reference-price column</label>
                    <select value={refCol ?? ''} onChange={(e) => setRefCol(e.target.value === '' ? null : Number(e.target.value))}>
                      <option value="">— 无 / none —</option>
                      {stage.columns.map((c) => <option key={c.col} value={c.col}>{colLabel(c)}</option>)}
                    </select>
                  </div>
                  <div className="field" style={{ flex: '0 0 130px', marginBottom: 0 }}>
                    <label>数据起始行 / Data start</label>
                    <input type="number" value={dataStart} onChange={(e) => setDataStart(Number(e.target.value))} />
                  </div>
                </div>
                <label className="small" style={{ color: 'var(--muted)' }}>报价列 / Bid columns（劳务方 + 轮次，可改）</label>
                <div className="card" style={{ padding: 10, marginTop: 4 }}>
                  {bidCols.length === 0 && <div className="small muted">未检测到报价列 — 用下方"+ 添加"手动指定，或勾选上方"仅导入工程量清单"。</div>}
                  {bidCols.map((bc, i) => (
                    <div key={i} className="row" style={{ gap: 6, marginBottom: 6 }}>
                      <select value={bc.col} onChange={(e) => setBidCols((v) => v.map((x, j) => j === i ? { ...x, col: Number(e.target.value) } : x))} style={{ flex: '1 1 160px' }}>
                        {stage.columns.map((c) => <option key={c.col} value={c.col}>{colLabel(c)}</option>)}
                      </select>
                      <input value={bc.labor_co_name} placeholder="劳务方 / name" onChange={(e) => setBidCols((v) => v.map((x, j) => j === i ? { ...x, labor_co_name: e.target.value } : x))} style={{ flex: '1 1 120px' }} />
                      <span className="small muted">R</span>
                      <input type="number" value={bc.round_number} onChange={(e) => setBidCols((v) => v.map((x, j) => j === i ? { ...x, round_number: Number(e.target.value) } : x))} style={{ width: 56 }} />
                      <button onClick={() => setBidCols((v) => v.filter((_, j) => j !== i))}>✕</button>
                    </div>
                  ))}
                  <button onClick={() => setBidCols((v) => [...v, { col: stage.columns[0]?.col ?? 0, labor_co_name: '', round_number: 1 }])} style={{ marginTop: 4 }}>+ 添加报价列 / Add</button>
                </div>
              </>
            )}

            <div className="row" style={{ marginTop: 12 }}>
              {fromPull
                ? <span className="small muted" title="工程量清单来自采购大脑拉取，无需重新上传 / this BOQ was pulled from 采购大脑">📥 采购大脑拉取 · 核对后提交 / pulled — review &amp; commit</span>
                : <button onClick={() => setStep(1)}>← 重新上传</button>}
              <span className="spacer" />
              <button className="btn-primary" disabled={busy || (!boqOnly && (itemCol == null || bidCols.length === 0))} onClick={() => commit()}>{busy ? '提交中…' : '提交导入 / Commit →'}</button>
            </div>
          </div>
        )}

        {step === 3 && result && (
          <div>
            <div className="card enter-pop" style={{ borderColor: 'var(--good)', background: 'var(--good-soft)' }}>
              <strong style={{ color: 'var(--good)' }}>✓ 导入成功 / Imported</strong>
              <div className="small" style={{ marginTop: 6 }}>
                {result.work_items} 工程项 / work items
                {!result.boq_only && <> · {result.labor_co_count ?? result.labor_cos} 劳务方 / contractors · {result.bids} 报价 / bids</>}
                {result.envelope_from_total != null && <> · 测算价合计 ¥{Math.round(result.envelope_from_total).toLocaleString()}</>}
              </div>
              <div className="small muted" style={{ marginTop: 6 }}>
                {result.boq_only
                  ? '已导入工程量清单 — 请在「工程量清单」标签核对、增删。/ 工程量清单 imported — review & edit it in the Work Items tab.'
                  : '清标已自动运行。关闭后在对比表查看。/ Cleaning ran automatically — see the comparison table.'}
              </div>
            </div>
            {/* 7/2 walkthrough (approved design): after a clear-bids re-import labor gets NO signal otherwise —
                offer the one-click notify. Deliberately manual: 改改 may re-import several times before it's ready. */}
            {(result.bids_archived ?? 0) > 0 && (
              <div className="card" style={{ marginTop: 10, borderColor: 'var(--warn)', background: 'var(--warn-soft)' }}>
                {/* D5: after a successful notify the ask + button disappear — a stale "尚未收到通知" beside a
                    re-enabled button double-sent the identical blast to every bidder. */}
                {notifiedMsg?.startsWith('✓') ? (
                  <div className="small" style={{ color: 'var(--good)' }}>{notifiedMsg}</div>
                ) : (
                  <>
                    <div className="small">
                      已存档 {result.bids_archived} 轮旧报价（对比表「比家比轮」可查看历史）。劳务尚未收到任何通知 —
                      清单确认无误后，请通知他们按新清单重新报价。/ {result.bids_archived} old round(s) archived; labor has NOT been notified yet.
                    </div>
                    <div className="row" style={{ marginTop: 8, alignItems: 'center', gap: 8 }}>
                      <button className="btn-primary" disabled={notifying} onClick={async () => {
                        setNotifying(true);
                        try { const r = await notifyReimport(pid); setNotifiedMsg(`✓ 已通知 ${r.notified} 家劳务重新报价`); }
                        catch (e: any) { setNotifiedMsg(`⚠ ${e?.body?.error ?? e?.message ?? '通知失败'}`); }
                        finally { setNotifying(false); }
                      }}>🔔 通知劳务重新报价 / Notify bidders</button>
                      {notifiedMsg && <span className="small" style={{ color: 'var(--bad)' }}>{notifiedMsg}</span>}
                    </div>
                  </>
                )}
              </div>
            )}
            <div className="row" style={{ marginTop: 12 }}>
              <span className="spacer" />
              <button className="btn-primary" onClick={onClose}>完成 / Done</button>
            </div>
          </div>
        )}
    </Modal>
  );
}
